import React, { useMemo, useCallback } from "react";
import { Box, Button } from "@mui/material";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import MotionWrapper from "../../../../../components/MotionWrapper";

const MotionButton = motion(Button);

const FilterCards = ({ setActiveTab, activeTab, formik }) => {
  const { goalStatuses, goalStatusFilterTabLoading } = useSelector(
    (state) => state.appraisal
  );

  // Memoize status color mapping
  const statusColorMap = useMemo(
    () => ({
      All: { bgc: "#e0f2ff", c: "#1976d2" },
      "In Progress": { bgc: "#dbf6e5", c: "#119272" },
      "Pending For Approval": { bgc: "#fff1d6", c: "#b76e00" },
      Completed: { bgc: "#dbf6e5", c: "#119272" },
      Overdue: { bgc: "#ffe4de", c: "#b71d18" },
      Rejected: { bgc: "#ffe4de", c: "#b71d18" },
      Cancelled: { bgc: "#ffe4de", c: "#b71d18" },
      "To Be Approved": { bgc: "#edeff1", c: "#777381" },
    }),
    []
  );

  // Memoize enriched goalStatuses
  const enrichedData = useMemo(() => {
    return goalStatuses.map((item) => ({
      ...item,
      ...statusColorMap[item.status],
    }));
  }, [goalStatuses, statusColorMap]);

  // Memoize field update handler
  const onTabChange = useCallback(
    (id) => {
      formik.setFieldValue("goalStatus", id);
    },
    [formik]
  );

  return (
    <Box
      display="flex"
      gap={2}
      sx={{
        p: 2,
        overflowX: "auto",
        whiteSpace: "nowrap",
        flexWrap: "nowrap",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {enrichedData.map((option, i) => {
        const isActive = formik.values.goalStatus === option.id;

        return (
          <Box key={option.id}>
            <MotionWrapper directions="bottom" delay={i * 0.1}>
              <MotionButton
                size="small"
                onClick={() => onTabChange(option.id)}
                sx={{
                  flexShrink: 0,
                  borderRadius: 4,
                  px: 2,
                  border: "1px solid",
                  borderColor: isActive ? "primary.main" : "transparent",
                  backgroundColor: isActive ? "white" : "transparent",
                  fontWeight: isActive ? 600 : 400,
                }}
                animate={{
                  scale: isActive ? 1.01 : 1,
                  boxShadow: isActive
                    ? "0 4px 12px rgba(33, 150, 243, 0.4)"
                    : "0 0 0 rgba(0,0,0,0)",
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
                variant="text"
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.97 }}
              >
                {option.status}
                <Box
                  component="span"
                  sx={{
                    backgroundColor: option.bgc,
                    color: option.c,
                    borderRadius: "8px",
                    px: 1.2,
                    ml: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  ({option.count})
                </Box>
              </MotionButton>
            </MotionWrapper>
          </Box>
        );
      })}
    </Box>
  );
};

export default React.memo(FilterCards);
